/**
 * @file CarrierPickupScopeFields.tsx
 * @description Выбор области синхронизации кэша ПВЗ (phase19.12).
 *
 * Три режима: вся география перевозчика / набор стран / точечный список
 * населённых пунктов. НП ищутся по справочнику перевозчика с debounce 400 мс
 * (от 2 символов) и добавляются чипсами.
 *
 * Значение — часть общей формы страницы настроек, сохраняется кнопкой
 * «Сохранить» вместе с остальными полями.
 */

import { Loader2, Search, X } from "lucide-react";
import { useEffect, useState } from "react";

import { Input } from "../ui/input";
import { ApiError } from "../../lib/api-client";
import {
  SDEK_SYNC_COUNTRIES,
  searchCarrierCities,
  type CarrierCitySuggestion,
  type CarrierKind,
  type PickupSyncScope,
  type PickupSyncScopeMode,
} from "../../lib/api-shop-carriers";

interface Props {
  carrier: CarrierKind;
  value: PickupSyncScope;
  onChange: (scope: PickupSyncScope) => void;
  disabled?: boolean;
}

const MODES: Array<{ mode: PickupSyncScopeMode; label: string }> = [
  { mode: "ALL", label: "Вся география перевозчика" },
  { mode: "COUNTRIES", label: "Выбранные страны" },
  { mode: "CITIES", label: "Населённые пункты" },
];

export function CarrierPickupScopeFields({ carrier, value, onChange, disabled }: Props) {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<CarrierCitySuggestion[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);

  const countries = value.countries ?? [];
  const cities = value.cities ?? [];

  // Поиск НП по справочнику: debounce 400 мс, запрос от 2 символов.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setSuggestions([]);
      setSearchError(null);
      return;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      setSearching(true);
      setSearchError(null);
      searchCarrierCities(carrier, q)
        .then((found) => {
          if (!cancelled) setSuggestions(found);
        })
        .catch((err: unknown) => {
          if (cancelled) return;
          setSuggestions([]);
          setSearchError(
            err instanceof ApiError ? err.message : "Не удалось найти населённый пункт",
          );
        })
        .finally(() => {
          if (!cancelled) setSearching(false);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [carrier, query]);

  /** Смена режима не теряет уже набранные страны и НП. */
  const setMode = (mode: PickupSyncScopeMode): void => onChange({ ...value, mode });

  const toggleCountry = (code: string): void => {
    const next = countries.includes(code)
      ? countries.filter((c) => c !== code)
      : [...countries, code];
    onChange({ ...value, countries: next });
  };

  const addCity = (item: CarrierCitySuggestion): void => {
    if (cities.some((c) => c.code === item.code)) return;
    onChange({ ...value, cities: [...cities, { code: item.code, name: item.city }] });
    setQuery("");
    setSuggestions([]);
  };

  const removeCity = (code: number): void =>
    onChange({ ...value, cities: cities.filter((c) => c.code !== code) });

  const freeSuggestions = suggestions.filter((s) => !cities.some((c) => c.code === s.code));

  return (
    <fieldset className="flex flex-col gap-2" disabled={disabled}>
      <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-on-surface-variant">
        Область синхронизации
      </legend>

      {MODES.map((m) => (
        <div key={m.mode} className="flex flex-col gap-2">
          <label className="flex min-h-11 items-center gap-2 text-sm">
            <input
              type="radio"
              name="pickupSyncScopeMode"
              className="size-4"
              checked={value.mode === m.mode}
              onChange={() => setMode(m.mode)}
            />
            {m.label}
          </label>

          {m.mode === "COUNTRIES" && value.mode === "COUNTRIES" && (
            <div className="grid grid-cols-1 gap-x-4 pl-6 sm:grid-cols-2 lg:grid-cols-3">
              {SDEK_SYNC_COUNTRIES.map((c) => (
                <label key={c.code} className="flex min-h-11 items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    className="size-4"
                    checked={countries.includes(c.code)}
                    onChange={() => toggleCountry(c.code)}
                  />
                  {c.label}
                </label>
              ))}
            </div>
          )}

          {m.mode === "CITIES" && value.mode === "CITIES" && (
            <div className="flex flex-col gap-2 pl-6">
              {cities.length > 0 && (
                <ul className="flex flex-wrap gap-1.5">
                  {cities.map((c) => (
                    <li key={c.code}>
                      <span className="flex min-h-11 items-center gap-1 rounded-full bg-surface-low pl-3 pr-1 text-sm sm:min-h-9">
                        {c.name}
                        <button
                          type="button"
                          aria-label={`Убрать ${c.name}`}
                          className="flex size-9 items-center justify-center rounded-full text-on-surface-variant hover:bg-accent hover:text-on-surface sm:size-8"
                          onClick={() => removeCity(c.code)}
                        >
                          <X className="size-4" />
                        </button>
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-on-surface-variant" />
                <Input
                  className="pl-8"
                  value={query}
                  placeholder="Поиск по справочнику перевозчика…"
                  aria-label="Поиск населённого пункта"
                  onChange={(e) => setQuery(e.target.value)}
                />
                {searching && (
                  <Loader2 className="absolute right-2.5 top-1/2 size-4 -translate-y-1/2 animate-spin text-on-surface-variant" />
                )}
                {freeSuggestions.length > 0 && (
                  <ul className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md bg-surface-lowest py-1 [box-shadow:0_8px_24px_rgba(42,47,26,0.12),inset_0_0_0_1px_var(--outline-variant)]">
                    {freeSuggestions.map((s) => (
                      <li key={s.code}>
                        <button
                          type="button"
                          className="flex min-h-11 w-full items-center px-3 text-left text-sm hover:bg-surface-low"
                          onClick={() => addCity(s)}
                        >
                          {s.city}
                          {s.region ? (
                            <span className="ml-1 text-on-surface-variant">({s.region})</span>
                          ) : null}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              {searchError && <p className="text-xs text-error">{searchError}</p>}
            </div>
          )}
        </div>
      ))}
    </fieldset>
  );
}
